// ============================================
// FILE: /frontend/src/components/listings/ChamaHowItWorks.jsx
// PURPOSE: Collapsible explainer for Group Buying / Chama model
// STYLE: Matches JV "How It Works" collapsible pattern
// ============================================

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { 
  ChevronDown, 
  X, 
  Users, 
  CreditCard, 
  ShieldCheck, 
  Clock, 
  TrendingUp,
  CheckCircle,
  AlertCircle,
  Banknote,
  Building
} from 'lucide-react';

export default function ChamaHowItWorks({ audience = 'buyer' }) {
  const [showWhatIsIt, setShowWhatIsIt] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [showFAQ, setShowFAQ] = useState(false);

  const isSeller = audience === 'seller';

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* What Is Group Buying - Collapsible */}
      <Card>
        <CardHeader 
          className="p-3 sm:p-4 md:p-6 cursor-pointer hover:bg-accent/50 transition-colors"
          onClick={() => setShowWhatIsIt(!showWhatIsIt)}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm sm:text-base md:text-lg flex items-center gap-2">
              <Users className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
              What is Group Buying?
            </CardTitle>
            {showWhatIsIt ? (
              <X className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
            )}
          </div>
        </CardHeader>

        {showWhatIsIt && (
          <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3">
              <div className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg bg-muted/40">
                <Building className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-xs sm:text-sm font-medium mb-0.5">Fractional Ownership</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Multiple buyers co-own expensive properties (KES 50M+) by purchasing shares instead of the full price.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg bg-muted/40">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-xs sm:text-sm font-medium mb-0.5">Lower Entry Barrier</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Invest in premium properties with smaller amounts. Own 1% or 10% based on your budget.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg bg-muted/40">
                <ShieldCheck className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-xs sm:text-sm font-medium mb-0.5">Secure Process</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    10% deposit reserves your shares. Full payment only after 100% funding is achieved.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg bg-muted/40">
                <Banknote className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-xs sm:text-sm font-medium mb-0.5">Refund Protection</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    If funding target isn't met within 4 months, all deposits are refunded within 7-14 days.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* How It Works - Collapsible */}
      <Card>
        <CardHeader 
          className="p-3 sm:p-4 md:p-6 cursor-pointer hover:bg-accent/50 transition-colors"
          onClick={() => setShowHowItWorks(!showHowItWorks)}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm sm:text-base md:text-lg">
              How It Works {isSeller ? '(For Sellers)' : '(For Buyers)'}
            </CardTitle>
            {showHowItWorks ? (
              <X className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
            )}
          </div>
        </CardHeader>

        {showHowItWorks && (
          <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
            <div className="space-y-2 sm:space-y-3">
              {isSeller ? (
                // SELLER STEPS
                <>
                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-purple-600 text-white flex items-center justify-center flex-shrink-0 text-xs sm:text-sm font-medium">
                      1
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-medium mb-0.5">Enable Group Buying</h4>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        When listing an eligible property (KES 50M+), toggle on "Enable Group Buying"
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-purple-600 text-white flex items-center justify-center flex-shrink-0 text-xs sm:text-sm font-medium">
                      2
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-medium mb-0.5">Shares Are Created</h4>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        System divides your property into 100 shares with a 4-month funding window
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-purple-600 text-white flex items-center justify-center flex-shrink-0 text-xs sm:text-sm font-medium">
                      3
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-medium mb-0.5">Buyers Reserve Shares</h4>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Interested buyers pay 10% deposits to reserve their shares
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-purple-600 text-white flex items-center justify-center flex-shrink-0 text-xs sm:text-sm font-medium">
                      4
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-medium mb-0.5">100% Funding Achieved</h4>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Once all shares are sold, buyers have 90 days to complete full payment
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-green-600 text-white flex items-center justify-center flex-shrink-0 text-xs sm:text-sm font-medium">
                      <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-medium mb-0.5">Receive Full Payment</h4>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        You receive the full property price once all buyers complete payment
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                // BUYER STEPS
                <>
                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-purple-600 text-white flex items-center justify-center flex-shrink-0 text-xs sm:text-sm font-medium">
                      1
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-medium mb-0.5">Find a Property</h4>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Browse listings with the "Group Buying Available" badge
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-purple-600 text-white flex items-center justify-center flex-shrink-0 text-xs sm:text-sm font-medium">
                      2
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-medium mb-0.5">Choose Your Shares</h4>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Select how many shares you want (e.g., 5 shares = 5% ownership)
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-purple-600 text-white flex items-center justify-center flex-shrink-0 text-xs sm:text-sm font-medium">
                      3
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-medium mb-0.5">Pay 10% Deposit</h4>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Reserve your shares by paying a 10% deposit (refundable if funding fails)
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-purple-600 text-white flex items-center justify-center flex-shrink-0 text-xs sm:text-sm font-medium">
                      4
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-medium mb-0.5">Wait for 100% Funding</h4>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Track progress as other buyers join. Funding window is 4 months.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-green-600 text-white flex items-center justify-center flex-shrink-0 text-xs sm:text-sm font-medium">
                      <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-medium mb-0.5">Complete Payment & Own</h4>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Pay remaining 90% within 90 days. Ownership transferred upon completion.
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {/* FAQ - Collapsible */}
      <Card>
        <CardHeader 
          className="p-3 sm:p-4 md:p-6 cursor-pointer hover:bg-accent/50 transition-colors"
          onClick={() => setShowFAQ(!showFAQ)}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm sm:text-base md:text-lg flex items-center gap-2">
              <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
              Frequently Asked Questions
            </CardTitle>
            {showFAQ ? (
              <X className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
            )}
          </div>
        </CardHeader>

        {showFAQ && (
          <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
            <div className="space-y-3 sm:space-y-4">
              <div className="border-b pb-2 sm:pb-3">
                <h4 className="text-xs sm:text-sm font-medium mb-1">
                  What properties are eligible?
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Properties priced at KES 50 million or above in these categories: Off Plan, Joint Ventures, Commercial Property, and Godowns/Warehouses.
                </p>
              </div>

              <div className="border-b pb-2 sm:pb-3">
                <h4 className="text-xs sm:text-sm font-medium mb-1">
                  What happens if funding target isn't met?
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  If 100% funding isn't achieved within 4 months, all deposits are refunded to buyers within 7-14 business days.
                </p>
              </div>

              <div className="border-b pb-2 sm:pb-3">
                <h4 className="text-xs sm:text-sm font-medium mb-1">
                  How is ownership structured?
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Each share represents 1% ownership. If you buy 10 shares, you own 10% of the property. Legal documentation reflects proportional ownership.
                </p>
              </div>

              <div className="border-b pb-2 sm:pb-3">
                <h4 className="text-xs sm:text-sm font-medium mb-1">
                  Can I sell my shares later?
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Yes, shares can be transferred or sold to other interested parties, subject to legal transfer procedures.
                </p>
              </div>

              <div>
                <h4 className="text-xs sm:text-sm font-medium mb-1">
                  Who manages the property?
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Co-owners typically appoint a property manager or form a management committee. Details are agreed upon after full funding.
                </p>
              </div>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}